import { useEffect, useState } from "react";
import { ExtractionDownload } from "../Extraction/ExtractionDownload";
import { ExtractionParameterTable } from "./ExtractionParameterTable";
import { ExtractionParameterUpdates } from "./ExtractionParameterUpdates";

interface ExtractionProps {
  extraction: Extraction;
  workspaceId: string;
  assistantId: string;
  syncAssistant: React.SetStateAction<any>;
  assistantType: string;
  locked: boolean;
}

function useExtractions(extraction) {
  const [extractions, setExtractions] = useState({
    extractionParameters: [],
    mandatoryFields: {
      fields: [],
      condition: "OR",
    },
  });

  useEffect(() => {
    const { extractionParameters, mandatoryFields } = extraction;
    const { fields } = mandatoryFields;

    const updatedExtractionParameters = extractionParameters.map(
      (extractionParameter) => {
        const isMandatoryField = Boolean(
          fields.includes(extractionParameter.parameterName)
        );
        return {
          ...extractionParameter,
          isMandatoryField,
        };
      }
    );

    setExtractions({
      extractionParameters: updatedExtractionParameters,
      mandatoryFields,
    });
  }, [extraction]);

  function serializeExtractions(extractionParameters) {
    return extractionParameters.map((extractionParameter) => {
      const { isMandatoryField, ...restExtractionParameter } =
        extractionParameter;
      return restExtractionParameter;
    });
  }

  function addExtractionParameter(extractionParameter) {
    const { isMandatoryField, ...restExtractionParameter } =
      extractionParameter;
    const { parameterName } = restExtractionParameter;

    setExtractions((prevExtractions) => {
      const { extractionParameters, mandatoryFields } = prevExtractions;
      const { fields, condition } = mandatoryFields;

      let updatedMandatoryFields = {
        condition,
        fields,
      };

      if (isMandatoryField) {
        updatedMandatoryFields = {
          condition,
          fields: [...fields, parameterName],
        };
      }

      return {
        extractionParameters: [...extractionParameters, extractionParameter],
        mandatoryFields: updatedMandatoryFields,
      };
    });
  }

  function deleteExtractionParameter(
    extractionParameter,
    extractionParameterIdx
  ) {
    const { isMandatoryField, ...restExtractionParameter } =
      extractionParameter;
    const { parameterName } = restExtractionParameter;

    setExtractions((prevExtractions) => {
      const { extractionParameters, mandatoryFields } = prevExtractions;
      const { fields, condition } = mandatoryFields;

      let updatedMandatoryFields = {
        fields,
        condition,
      };

      if (isMandatoryField) {
        const updatedFields = fields.filter((field) => field !== parameterName);
        updatedMandatoryFields = {
          fields: updatedFields,
          condition,
        };
      }

      const nextExtractions = extractionParameters.filter(
        (_, idx) => idx != extractionParameterIdx
      );

      return {
        extractionParameters: nextExtractions,
        mandatoryFields: updatedMandatoryFields,
      };
    });
  }

  function updateExtractionParameter(
    extractionParameter,
    extractionParameterIdx
  ) {
    const { isMandatoryField, ...restExtractionParameter } =
      extractionParameter;
    const { parameterName } = restExtractionParameter;

    setExtractions((prevExtractions) => {
      const { extractionParameters, mandatoryFields } = prevExtractions;
      const { fields, condition } = mandatoryFields;

      let updatedMandatoryFields = {
        fields,
        condition,
      };

      const isAlreadyAMandatoryField = Boolean(fields.includes(parameterName));

      if (isMandatoryField && !isAlreadyAMandatoryField) {
        updatedMandatoryFields = {
          fields: [...fields, parameterName],
          condition,
        };
      }

      if (!isMandatoryField && isAlreadyAMandatoryField) {
        const updatedFields = fields.filter((field) => field !== parameterName);

        updatedMandatoryFields = {
          fields: updatedFields,
          condition,
        };
      }

      const nextExtractionParameters = extractionParameters.map(
        (_extractionParameter, idx) => {
          if (idx === extractionParameterIdx) {
            return extractionParameter;
          }

          return _extractionParameter;
        }
      );

      return {
        extractionParameters: nextExtractionParameters,
        mandatoryFields: updatedMandatoryFields,
      };
    });
  }

  function updateMandatoryFieldCondition(condition) {
    setExtractions((prevExtractions) => {
      const { mandatoryFields } = prevExtractions;
      const { fields } = mandatoryFields;
      return {
        ...prevExtractions,
        mandatoryFields: {
          fields,
          condition,
        },
      };
    });
  }

  return {
    ...extractions,
    serializeExtractions,
    addExtractionParameter,
    deleteExtractionParameter,
    updateExtractionParameter,
    updateMandatoryFieldCondition,
  };
}

function Extractions(props: ExtractionProps) {
  const {
    workspaceId,
    extraction,
    assistantId,
    syncAssistant,
    assistantType,
    locked,
  } = props;
  const {
    extractionParameters,
    mandatoryFields,
    serializeExtractions,
    addExtractionParameter,
    deleteExtractionParameter,
    updateExtractionParameter,
    updateMandatoryFieldCondition,
  } = useExtractions(extraction);

  return (
    <div className="flex flex-col gap-y-8 border rounded-lg m-4">
      <div className="px-6 py-4 bg-gray-50 rounded-t-lg w-full">
        <div className="flex items-center justify-between">
          <p className="font-semibold text-md">
            Extractions ({extractionParameters.length})
          </p>
          <div className="flex gap-x-2 items-center">
            <ExtractionDownload
              projectId={workspaceId}
              assistantId={assistantId}
              assistantType={assistantType}
            />
            {!locked && (
              <ExtractionParameterUpdates
                workspaceId={workspaceId}
                assistantId={assistantId}
                syncAssistant={syncAssistant}
                mandatoryFields={mandatoryFields}
                extractionParameters={extractionParameters}
                serializeExtractions={serializeExtractions}
                onAddExtractionParameter={addExtractionParameter}
                onDeleteExtractionParameter={deleteExtractionParameter}
                onUpdateExtractionParameter={updateExtractionParameter}
                onUpdateMandatoryFieldCondition={updateMandatoryFieldCondition}
              />
            )}
          </div>
        </div>
        {/* <p className="text-sm text-gray-500">Specify the fields to be captured from the conversation</p> */}
      </div>
      <div className="min-h-96 px-6 overflow-auto">
        <ExtractionParameterTable
          extractionParameters={extractionParameters}
          showActions={false}
        />
      </div>
    </div>
  );
}

export { Extractions };
